import { useLayoutEffect, useState } from "react";
import type { AnimationDirection } from "web_myplann/Dashboard/Hooks/useDashboardAnimation";

type SlotIndex = 0 | 1;

const getEnterTransform = (direction: AnimationDirection) =>
	direction === "left" ? "translateX(-100%)" : "translateX(100%)";

const getExitTransform = (direction: AnimationDirection) =>
	direction === "left" ? "translateX(100%)" : "translateX(-100%)";

export const useDashboardCardSlots = <T extends { id: number }>(
	dashboard: T,
	direction: AnimationDirection,
) => {
	const [slots, setSlots] = useState<[T, T]>([dashboard, dashboard]);
	const [frontSlot, setFrontSlot] = useState<SlotIndex>(0);
	const [isEntering, setIsEntering] = useState(false);
	const [lastId, setLastId] = useState(dashboard.id);
	const [lastDirection, setLastDirection] = useState<AnimationDirection>(null);

	const setSlot = (index: SlotIndex, value: T) =>
		setSlots((current) =>
			index === 0 ? [value, current[1]] : [current[0], value],
		);

	if (dashboard.id !== lastId) {
		if (direction === null) {
			setSlot(frontSlot, dashboard);
			setLastId(dashboard.id);
		} else {
			const nextFront: SlotIndex = frontSlot === 0 ? 1 : 0;
			setSlot(nextFront, dashboard);
			setFrontSlot(nextFront);
			setIsEntering(true);
			setLastDirection(direction);
			setLastId(dashboard.id);
		}
	} else if (slots[frontSlot] !== dashboard) {
		setSlot(frontSlot, dashboard);
	}

	useLayoutEffect(() => {
		if (!isEntering) {
			return;
		}
		let secondFrame = 0;
		const firstFrame = requestAnimationFrame(() => {
			secondFrame = requestAnimationFrame(() => setIsEntering(false));
		});
		return () => {
			cancelAnimationFrame(firstFrame);
			cancelAnimationFrame(secondFrame);
		};
	}, [isEntering]);

	const getSlotTransform = (index: SlotIndex) => {
		if (index === frontSlot) {
			return isEntering ? getEnterTransform(lastDirection) : "translateX(0)";
		}
		return isEntering ? "translateX(0)" : getExitTransform(lastDirection);
	};

	return { slots, frontSlot, isEntering, getSlotTransform };
};
